<template>
    <m-pageframe
        title="{organization-label} Bulk Messages"
        :color="pageframe_color"
    >

        <v-container
            style="padding-left: 25px; padding-right: 25px;"
            fluid
        >

            <m-incident-banner
                ref="incident-banner"
                page="bulk_message"
            />
            
            <provision-warning-bulk-messages />

            <bulk-revamp-awareness />

            <m-help-banner
                label="Need Help Creating or Sending a Bulk Message?  Click Here for Help!"
                url="https://redchirp.com/?p=7167"
            />

            <v-text-field
                autocomplete="off"
                label="Search"
                prepend-icon="mdi-magnify"
                v-model="full_text"
                v-on:input="doSearch"
            >
                <v-menu
                    bottom
                    offset-y
                    slot="append-outer"
                >
                    <template v-slot:activator="{ on }">
                        <v-icon v-on="on">
                            mdi-dots-vertical
                        </v-icon>
                    </template>

                    <v-list>
                        <v-list-item
                            v-on:click="onClickCreateBulkMessageFromTemplate()"
                            v-if="are_templates_available"
                        >
                            <v-list-item-title>Create Bulk Message From Template...</v-list-item-title>
                        </v-list-item>

                        <v-list-item v-on:click="onClickCreateBulkMessage()">
                            <v-list-item-title>Create Custom Bulk Message...</v-list-item-title>
                        </v-list-item>

                        <!-- Admin-only item -->
                        <m-restricted-menu-list-item
                            v-if="$rc.$state.isAuthActorSystemAdmin()"
                            v-on:click="$rc.$state.isAuthActorSystemAdmin() ? navigateToProvision() : null"                            
                            text="Disable sending bulk messages..."
                        >
                            <v-list-item-icon><v-icon>mdi-arrow-right</v-icon></v-list-item-icon>
                        </m-restricted-menu-list-item>

                    </v-list>
                </v-menu>
            </v-text-field>



            <m-edit-tag-filter
                label="Tags"
                applies_to="bulk-message"
                v-model="tag_filter"
                v-on:input="doSearch"
            />

            <v-layout
                row
                wrap
                style="margin-bottom: 15px;"
            >
                <v-flex flex-grow-0>
                    <v-checkbox
                        label="Include archived bulk messages"
                        v-model="include_archived"
                        v-on:change="doSearch"
                    />
                </v-flex>

                <v-flex
                    flex-grow-1
                    style="margin-left: 15px"
                >
                    <v-checkbox
                        label="Show Bulk Message Analytics Group"
                        v-model="show_groups"
                    />
                </v-flex>


                <v-flex
                    flex-grow-0
                    align-self-center
                    style="text-align: right"
                >
                    <v-btn
                        v-on:click="navigateToOptOuts"
                        style="margin-right: 15px"
                    >
                        Opt Outs
                    </v-btn>
                </v-flex>

                <v-flex
                    flex-grow-0
                    align-self-center
                    style="text-align: right; margin-right: 15px"
                >
                    <v-btn v-on:click="onClickValidation">
                        Validation
                    </v-btn>
                </v-flex>

                <v-flex
                    flex-grow-0
                    align-self-center
                    style="text-align: right"
                >
                    <v-btn v-on:click="onClickAnalytics">
                        Analytics
                    </v-btn>
                </v-flex>

                <v-flex
                    flex-grow-0
                    align-self-center
                    style="text-align: right; margin-left: 15px;"
                    v-if="are_templates_available"
                >
                    <v-btn
                        v-on:click="onClickCreateBulkMessageFromTemplate"
                        style="margin-right: 15px"
                        color="primary"
                    >
                        Templates
                    </v-btn>
                </v-flex>

            </v-layout>

            <v-data-table
                :headers="headers"
                :items="bulk_messages"
                :items-per-page="1000"
                class="elevation-1"
                hide-default-footer
                v-on:click:row="onClickRow"
            >
                <template v-slot:item.tags="{ item }">
                    <m-rc-tag-display :value="item.rc_tags" />
                </template>

                <template v-slot:item.creation_date="{ item }">
                    {{ $rc.pretty(item.creation_time, 'date') }}
                </template>

                <template v-slot:item.label="{ item }">

                    {{ item.label }}

                    <v-chip
                        v-if="item.scheduled_send_time != null && item.scheduled_send_time > Date.now()"
                        label
                        dense
                        color="green lighten-4"
                    >
                        Scheduled {{ $rc.pretty(item.scheduled_send_time, 'date') }} @
                        {{ $rc.pretty(item.scheduled_send_time, 'time') }}
                    </v-chip>

                </template>

                <template v-slot:item.sent_date="{ item }">
                    {{ $rc.pretty(item.sent_time, 'date') }}
                </template>

                <template v-slot:item.creation_time="{ item }">
                    {{ $rc.pretty(item.creation_time, 'time') }}
                </template>

                <template v-slot:item.sent_time="{ item }">
                    {{ $rc.pretty(item.sent_time, 'time') }}
                </template>

                <template v-slot:item.recipient_count_sent="{ item }">
                    {{ item.state == 'edit' ? null : item.recipient_count_sent }}
                </template>

                <template v-slot:item.recipient_count_filtered="{ item }">
                    {{ item.state == 'edit' ? null : item.recipient_count_filtered }}
                </template>

                <template v-slot:item.recipient_count_failed="{ item }">
                    {{ item.state == 'edit' ? null : item.recipient_count_failed }}
                </template>

                <template v-slot:item.bulk_message_group_label="{ item }">
                    <v-menu
                        bottom
                        offset-y
                    >

                        <template v-slot:activator="{ on }">

                            <a
                                v-on="on"
                                style="text-decoration: underline"
                            >
                                {{ item.bulk_message_group_label == null ? 'No analytics group' :
                                    item.bulk_message_group_label }}
                            </a>

                        </template>

                        <v-list>
                            <v-list-item v-on:click="onClickAddBulkMessageToExistingGroup(item)">
                                <v-list-item-title>Add bulk message to existing group...</v-list-item-title>
                            </v-list-item>

                            <v-list-item v-on:click="onClickAddBulkMessageToNewGroup(item)">
                                <v-list-item-title>Add bulk message to new group...</v-list-item-title>
                            </v-list-item>

                            <v-list-item
                                v-on:click="onClickRemoveBulkMessageFromGroup(item)"
                                :disabled="item.bulk_message_group_label == null"
                            >
                                <v-list-item-title>Remove bulk message from group</v-list-item-title>
                            </v-list-item>

                            <v-divider />

                            <v-list-item
                                v-on:click="onClickRenameGroup(item)"
                                :disabled="item.bulk_message_group_label == null"
                            >
                                <v-list-item-title>Rename group...</v-list-item-title>
                            </v-list-item>

                            <v-list-item
                                v-on:click="onClickDeleteGroup(item)"
                                :disabled="item.bulk_message_group_label == null"
                            >
                                <v-list-item-title>Delete group</v-list-item-title>
                            </v-list-item>

                        </v-list>

                    </v-menu>


                </template>



                <template v-slot:item.use_legacy_variables="{ item }">
                    <v-simple-checkbox
                        :value="item.use_legacy_variables"
                        :ripple="false"
                        readonly
                    />
                </template>



                <template v-slot:item.action="{ item }">
                    <v-menu
                        bottom
                        offset-y
                    >

                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                v-bind="attrs"
                                v-on="on"
                            >
                                mdi-dots-vertical-circle-outline
                            </v-icon>
                        </template>

                        <v-list>
                            <v-list-item v-on:click="onViewDetails(item)">
                                <v-list-item-title>View Bulk Message</v-list-item-title>
                            </v-list-item>

                            <v-list-item v-on:click="onClickUpdateCounts(item)">
                                <v-list-item-title>Update Recipient Counts</v-list-item-title>
                            </v-list-item>

                            <v-list-item v-on:click="onClickEditTags(item)">
                                <v-list-item-title>Edit Tags...</v-list-item-title>
                            </v-list-item>


                            <v-list-item v-on:click="onCopyBulkMessage(item)">
                                <v-list-item-title>Copy Bulk Message</v-list-item-title>
                            </v-list-item>

                            <v-list-item v-on:click="changeArchiveStatus(item)">
                                <v-list-item-title>{{ changeArchiveStatusText(item) }}</v-list-item-title>
                            </v-list-item>


                            <template v-if="item.sent_time == null">
                                <v-divider />

                                <v-list-item v-on:click="onClickSplit(item)">
                                    <v-list-item-title>Split...</v-list-item-title>
                                </v-list-item>
                            </template>

                            <v-divider />

                            <v-list-item
                                :disabled="item.sent_time == null && item.scheduled_send_time == null"
                                v-on:click="onViewSendSummary(item)"
                            >
                                <v-list-item-title>Send Status...</v-list-item-title>
                            </v-list-item>

                            <v-divider />

                            <v-list-item
                                v-on:click="onClickOrderAttribution(item)"
                                :disabled="item.sent_time == null"
                            >
                                <v-list-item-title>Order Attribution...</v-list-item-title>
                            </v-list-item>

                        </v-list>
                    </v-menu>
                </template>

            </v-data-table>

            <div style="margin-top: 10px; font-size: 12px">
                Timezone: {{ Intl.DateTimeFormat().resolvedOptions().timeZone }}
            </div>

            <div
                v-if="!are_templates_available"
                style="text-align: right; margin-top: 15px"
            >
                <v-btn
                    class="mx-2"
                    fab
                    dark
                    small
                    color="primary"
                    v-on:click="onClickCreateBulkMessage"
                >
                    <v-icon dark>mdi-plus</v-icon>
                </v-btn>
            </div>
            <div
                v-else
                style="text-align: right; margin-top: 15px"
            >
                <v-menu
                    bottom
                    offset-y
                >

                    <template v-slot:activator="{ on }">

                        <v-btn
                            class="mx-2"
                            fab
                            dark
                            small
                            color="primary"
                            v-on="on"
                        >
                            <v-icon dark>mdi-plus</v-icon>
                        </v-btn>

                    </template>

                    <v-list>

                        <v-list-item
                            v-on:click="onClickCreateBulkMessageFromTemplate()"
                            v-if="are_templates_available"
                        >
                            <v-list-item-title>Create Bulk Message From Template...</v-list-item-title>
                        </v-list-item>

                        <v-list-item v-on:click="onClickCreateBulkMessage()">
                            <v-list-item-title>Create Custom Bulk Message...</v-list-item-title>
                        </v-list-item>

                    </v-list>

                </v-menu>
            </div>


            <m-create-bulk-message-dialog
                ref="bulk-message-dialog"
                v-on:upsert_bulk_message="afterCreateBulkMessage"
            />

            <!-- copy bulk message dialog -->
            <v-dialog
                v-model="copy_dialog"
                max-width="950"
            >
                <bulk-creation-inputs
                    ref="bulk_creation_inputs"
                    title="Copy Bulk Message"
                    :allow_label_edit="false"
                    :bulk_message_category="copy_bulk_message_category"
                    :bulk_message_filter="copy_bulk_message_filter"
                    @update:bulk_message_category="copy_bulk_message_category = $event"
                    @update:bulk_message_filter="copy_bulk_message_filter = $event"
                    @ok="onCopyOk"
                    @cancel="copy_dialog = false"
                >
                    <template #before-inputs>
                        <div class="mb-4">
                            <p
                                id="copy-recipients"
                                class="mb-2"
                            >
                                Copy recipients that are...
                            </p>
                            <v-checkbox
                                v-for="choice in $rc.enumChoices('bulk_message_recipient_state', false)"
                                v-model="recipients_to_copy"
                                :key="choice.value"
                                :label="choice.text"
                                :value="choice.value"
                                aria-labelledby="copy-recipients"
                                class="pl-3"
                                dense
                                hide-details
                            />
                            <div class="mt-3 pl-3">
                                <v-btn
                                    class="mr-2"
                                    color="primary"
                                    outlined
                                    small
                                    @click="selectAllRecipients"
                                >
                                    Select All
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    outlined
                                    small
                                    @click="deselectAllRecipients"
                                >
                                    Deselect All
                                </v-btn>
                            </div>

                            <v-divider class="mt-4" />
                        </div>
                    </template>
                </bulk-creation-inputs>
            </v-dialog>

        </v-container>
    </m-pageframe>
</template>

<script>
import * as Time from '@/js/time.js';
import BulkRevampAwareness from '@/components/bulk/bulk-revamp-awareness.vue';
import BulkCreationInputs from "@/views/bulk/bulk-creation-inputs.vue";
import ProvisionWarningBulkMessages from '@/components/bulk/provision-warning-bulk-messages.vue';

export default {
    components: {
        BulkRevampAwareness,
        BulkCreationInputs,
        ProvisionWarningBulkMessages
    },

    data() {
        return {
            organization_name: null,
            full_text: '',
            bulk_messages: [],
            include_archived: false,

            copying_item: null,
            copy_bulk_message_category: '',
            copy_bulk_message_filter: '',
            copy_dialog: false,
            recipients_to_copy: [],

            tag_filter: { 'type': 'includes-any', 'values': [] },
            show_groups: false,
        }
    },

    mounted() {
        this.getOrganizationName();
        this.doSearch();
    },

    methods: {
        changeArchiveStatusText(item) {
            if (item.is_archived) {
                return 'Unarchive Bulk Message';
            }

            return 'Archive Bulk Message';
        },

        changeArchiveStatus(item) {
            this.$rc.patchBulkMessage(
                item.bulk_message_id,
                {
                    'is_archived': !item.is_archived,
                },
                (data) => {
                    this.doSearch();
                }
            );
        },

        navigateToOptOuts() {
            this.$router.push({
                'name': 'search-consent-register',
                'params': {
                    'organization_id': this.$rc.$state.organization_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        prettyDateAndTime(time) {
            if (time == null || time == 0) return '';
            return Time.getISODate(time) + ' ' + Time.getPeriodClockTime(time);
        },

        getOrganizationName() {
            this.$rc.getOrganizationLabel(
                this.$route.params.organization_id,

                (label) => {
                    this.organization_name = label;
                }
            );
        },

        doSearch() {
            let search = {
                'organization_id': this.$route.params.organization_id,
                'full_text': this.full_text,
                'rc_tag_ids': this.tag_filter.values.length == 0 ? null : this.tag_filter
            };

            if (!this.include_archived) {
                search.is_archived = false;
            }

            this.$rc.showSpinner();

            this.$rc.listBulkMessages(
                search,
                (data) => {
                    this.$rc.hideSpinner();
                    this.bulk_messages = data.results;
                },

                () => {
                    this.$rc.hideSpinner();
                    this.bulk_messages = [];
                }
            );
        },

        onClickRow(item) {
            this.onViewDetails(item);
        },

        onCopyBulkMessage(item) {
            this.recipients_to_copy = [];
            this.copy_bulk_message_category = '';
            this.copy_bulk_message_filter = '';
            this.copying_item = item;

            if (this.copying_item.state === 'cancelled') {
                this.recipients_to_copy.push('pending');
                this.recipients_to_copy.push('cancelled');
            }

            this.copy_dialog = true;
        },

        onCopyOk() {
            const url = 'POST: /api/v1/bulk/messages/copy-v3';

            this.$rc.rest({
                spinner: false,
                endpoint: url,

                params: {
                    organization_id: this.copying_item.organization_id,
                    src_bulk_message_id: this.copying_item.bulk_message_id,
                    only_include_recipient_states: this.recipients_to_copy,
                    category: this.copy_bulk_message_category,
                    filter_type_on_send_to_recipients_when: this.copy_bulk_message_filter
                },

                on_success: (data) => {
                    this.copy_dialog = false;
                    this.doSearch();
                },

                on_error: 'There was an error copying the bulk message.',
            });
        },

        selectAllRecipients() {
            this.recipients_to_copy = this.$rc.enumChoices('bulk_message_recipient_state', false).map(choice => choice.value);
        },

        deselectAllRecipients() {
            this.recipients_to_copy = [];
        },

        onClickOrderAttribution(item) {
            this.$router.push({
                'name': 'analytics-one-bulk-message-analytics-group',
                'params': {
                    'organization_id': item.organization_id,
                    'analytics_group_id': item.comp_analytics_group_id,
                }
            });
        },

        onClickAnalytics() {
            this.$router.push({
                'name': 'analytics-all-bulk-messages',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                }
            });
        },

        onViewDetails(item) {
            this.$router.push({
                'name': 'view-bulk-message',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                    'bulk_message_id': item.bulk_message_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        onViewSendSummary(item) {
            this.$router.push({
                'name': 'view-bulk-message-send-summary',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                    'bulk_message_id': item.bulk_message_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        onClickCreateBulkMessage() {
            this.$refs['bulk-message-dialog'].showDialog(
                this.$route.params.organization_id
            );
        },

        onClickCreateBulkMessageFromTemplate() {
            this.$router.push({
                'name': 'bulk-message-template-create-page',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                }
            });
        },

        afterCreateBulkMessage(item) {
            this.$router.push({
                'name': 'view-bulk-message',
                'params': {
                    'organization_id': item.organization_id,
                    'bulk_message_id': item.bulk_message_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        onClickValidation() {
            this.$router.push({
                'name': 'search-endpoint-validation-details',
                'params': {
                    'organization_id': this.$route.params.organization_id
                }
            });
        },

        onClickEditTags(item) {
            this.$rc.change_tags_dialog({
                'organization_id': item.organization_id,
                'applies_to': 'bulk-message',
                'selected_tag_ids': item.rc_tag_ids,
                'show_archived': false,
                'on_save': (ids) => {

                    this.$rc.showSpinner();

                    this.$rc.rest(
                        'PUT: /api/v1/bulk/tags',

                        {
                            'bulk_message_id': item.bulk_message_id,
                            'rc_tag_ids': ids
                        },

                        (data) => {
                            this.$rc.hideSpinner();

                            this.doSearch();
                        },

                        () => {
                            this.$rc.hideSpinner();
                            this.$rc.alert('Error', 'Unable to edit bulk message tags');
                        },
                    );

                }
            });
        },

        onClickSplit(item) {
            if (item.scheduled_send_time != null) {
                this.$rc.alert('Unable to split bulk message', 'You may not split a bulk message that has a scheduled send time.<br/><br/>To split this bulk message, first cancel the scheduled send time (edit the message, scroll to the bottom and click the cancel scheduled send button).');
                return;
            }

            this.$rc.prompt({
                'title': 'Split bulk message',
                'label': 'Enter the number of parts to split the bulk message into (2 to 20)',
                'value': 2,
                'after_prompt': (value) => {
                    this.doSplit(item, parseInt(value));
                },

                'is_valid': (value) => {
                    if (value == null) return false;

                    let as_int = parseInt(value);

                    if (isNaN(as_int))
                        return false;

                    if (value < 2) return false;
                    if (value > 20) return false;

                    return true;
                }
            });
        },

        doSplit(item, part_count) {
            this.$rc.startProcess({
                'type': 'bulk-message-split',

                'params': {
                    'bulk_message_id': item.bulk_message_id,
                    'part_count': part_count,
                },

                'title': 'Splitting ' + item.label + ' into ' + part_count + ' parts',
                'finished_button_text': 'Ok',
                'failed_button_text': 'Ok',

                status: (pte) => {
                    switch (pte.state) {
                        case 'failed': return 'Unable to split bulk message';
                        case 'finished': return 'Your bulk message has been split ino ' + part_count + ' parts.  The original message has been archived.';
                    }

                    return 'Bulk Message recipients split: ' + pte.progress_counter;
                },

                on_finished: (pte) => {
                    this.doSearch();
                },

                on_failed: (pte) => {
                    this.doSearch();
                },

            });
        },

        onClickUpdateCounts(item) {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/bulk/update-recipient-counts',
                'timeout': 60000,

                'params': {
                    'bulk_message_id': item.bulk_message_id,
                },

                'on_success': (data) => {
                    this.doSearch();
                },

                'on_error': 'Unable to update recipient counts',
            });
        },

        onClickAddBulkMessageToNewGroup(item) {
            this.$rc.prompt({
                'title': 'Create new bulk message analytics group',
                'label': 'Name of new analytics group',
                'value': null,
                'unset_label': null,

                'after_prompt': (value) => {
                    this.doAddBulkMessageToNewGroup(item, value);
                },
            });
        },

        doAddBulkMessageToNewGroup(item, bulk_message_group_label) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/bulk-message-group',

                'params': {
                    'organization_id': item.organization_id,
                    'bulk_message_id': item.bulk_message_id,
                    'bulk_message_group_id': this.$rc.uuidv4(),
                    'bulk_message_group_label': bulk_message_group_label,
                },

                'on_success': (data) => {
                    this.doSearch();
                },

                'on_error': 'Unable to create new bulk message group',
            });
        },

        onClickAddBulkMessageToExistingGroup(item) {
            this.$rc.pick({
                'title': 'Add to Bulk Message Analytics Group',
                'content_before': '',
                'content_after': null,

                'choices': 'bulk_message_group',

                'multiple': false,
                'type': 'select',
                'select_label': 'Choose One',
                'initial_value': null,
                'unset_label': null,

                'on_ok': (value) => {
                    this.doAddBulkMessageToExistingGroup(item, value);
                },

                'on_cancel': () => { }

            });
        },

        doAddBulkMessageToExistingGroup(item, bulk_message_group_id) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/bulk-message-group/add-bulk-message-to-group',

                'params': {
                    'bulk_message_id': item.bulk_message_id,
                    'bulk_message_group_id': bulk_message_group_id,
                },

                'on_success': (data) => {
                    this.doSearch();
                },

                'on_error': 'Unable to add bulk message to group',
            });
        },

        onClickRemoveBulkMessageFromGroup(item) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/bulk-message-group/remove-bulk-message-from-group',

                'params': {
                    'bulk_message_id': item.bulk_message_id,
                },

                'on_success': (data) => {
                    this.doSearch();
                },

                'on_error': 'Unable to remove bulk message from group',
            });
        },

        onClickRenameGroup(item) {
            this.$rc.prompt({
                'title': 'Rename Bulk Message Analytics Group',
                'label': 'Name of new analytics group',
                'value': item.bulk_message_group_label,
                'unset_label': null,

                'after_prompt': (value) => {
                    this.doRenameGroup(item, value);
                },
            });
        },

        doRenameGroup(item, bulk_message_group_label) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/bulk-message-group',

                'params': {
                    'organization_id': item.organization_id,
                    'bulk_message_group_id': item.bulk_message_group_id,
                    'bulk_message_group_label': bulk_message_group_label,
                },

                'on_success': (data) => {
                    this.doSearch();
                },

                'on_error': 'Unable to rename bulk message group',
            });
        },

        onClickDeleteGroup(item) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'DELETE: /api/v1/bulk/bulk-message-group',

                'params': {
                    'bulk_message_group_id': item.bulk_message_group_id,
                },

                'on_success': (data) => {
                    this.doSearch()
                },

                'on_error': 'Unable to delete bulk message group',
            });
        },

        navigateToProvision() {
            this.$router.push({
                'name': 'provision',
                'params': {
                    'organization_id': this.$rc.$state.organization_id,
                }
            }).catch(e => { }); // swallow any exception
        },
    },

    watch: {
        full_text(newValue, oldValue) {
            this.doSearch();
        }
    },

    computed: {

        pageframe_color() {
            if (this.$route.params.organization_id !== this.$rc.$state.organization_id)
                return '#5D647C';
            else
                return '#F24653';
        },

        headers() {

            let ret = [];

            ret.push({ text: 'Tags', value: 'tags' });

            if (this.show_groups) {
                ret.push({ text: 'Analytics Group', value: 'bulk_message_group_label' });
            }

            ret.push({ text: 'Name', value: 'label' });
            ret.push({ text: 'Recipients', value: 'recipient_count' });
            ret.push({ text: 'Messages Sent', value: 'recipient_count_sent' });
            ret.push({ text: 'Filtered', value: 'recipient_count_filtered' });
            ret.push({ text: 'Failed', value: 'recipient_count_failed' });
            ret.push({ text: 'State', value: 'state' });
            ret.push({ text: 'Created (date)', value: 'creation_date' });
            ret.push({ text: 'Sent (date)', value: 'sent_date' });
            ret.push({ text: 'Sent (by)', value: 'sent_by_employee_label' });

            ret.push({ text: 'Action', value: 'action' });

            return ret;
        },

        are_templates_available() {
            return this.$rc.$state.profile.organization_account.industry == 'wine';
        },
    },

}
</script>

<style lang="scss" scoped></style>
